import { Search, ShieldCheck } from 'lucide-react';
import { useDeferredValue, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageHeader } from '../components/AppShell';
import { BulkBar, toggleAll, toggleId } from '../components/BulkBar';
import { ExportMenu } from '../components/ExportMenu';
import { Topbar } from '../components/Topbar';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Empty } from '../components/ui/empty';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Spinner } from '../components/ui/spinner';
import { useConfirm } from '../components/ui/confirm';
import { useToast } from '../components/ui/toast';
import { exportUsers } from '../lib/api/export';
import { useBulkDeleteUsers, useSetUserRole, useUsers } from '../lib/api/users';
import { useAuthStore } from '../lib/auth/store';
import { fmtRelative } from '../lib/date';
import type { Role } from '../lib/types';

export default function Users(): JSX.Element {
  const me = useAuthStore((s) => s.user);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'all' | Role>('all');
  const [pendingOnly, setPendingOnly] = useState(false);
  const [page, setPage] = useState(1);
  const pageSize = 50;
  const deferredEmail = useDeferredValue(email);

  const list = useUsers({
    email: deferredEmail.trim() || undefined,
    role: role === 'all' ? undefined : role,
    deletionRequested: pendingOnly ? 'true' : undefined,
    page,
    pageSize,
  });

  const setRoleMut = useSetUserRole();
  const bulkDelete = useBulkDeleteUsers();
  const { showToast } = useToast();
  const { confirm, confirmDialog } = useConfirm();
  const navigate = useNavigate();
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const total = list.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const pageIds = (list.data?.items ?? []).map((u) => u.id);

  return (
    <>
      <Topbar title="Users" />
      <PageHeader
        title="Users"
        subtitle={`${total.toLocaleString()} user${total === 1 ? '' : 's'}`}
        actions={
          <ExportMenu
            options={[
              {
                format: 'csv',
                label: 'CSV (current filters)',
                run: () =>
                  exportUsers({
                    format: 'csv',
                    email: deferredEmail.trim() || undefined,
                    role: role === 'all' ? undefined : role,
                  }),
              },
              {
                format: 'json',
                label: 'JSON (current filters)',
                run: () =>
                  exportUsers({
                    format: 'json',
                    email: deferredEmail.trim() || undefined,
                    role: role === 'all' ? undefined : role,
                  }),
              },
            ]}
          />
        }
      />
      <div className="space-y-4 px-6 pb-10">
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-line bg-surface p-3 shadow-card">
          <div className="relative min-w-[260px] flex-1">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-subtle" />
            <Input
              placeholder="Filter by email…"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setPage(1);
              }}
              className="pl-8"
            />
          </div>
          <Select
            value={role}
            onValueChange={(v) => {
              setRole(v as typeof role);
              setPage(1);
            }}
          >
            <SelectTrigger className="w-[160px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All roles</SelectItem>
              <SelectItem value="user">User</SelectItem>
              <SelectItem value="correspondent">Correspondent</SelectItem>
              <SelectItem value="admin">Admin</SelectItem>
            </SelectContent>
          </Select>
          {/* Staff who deleted their account in an app — an admin completes
              it by deleting them (select + Delete) within 7 days. */}
          <Button
            variant={pendingOnly ? 'primary' : 'outline'}
            size="sm"
            onClick={() => {
              setPendingOnly((v) => !v);
              setPage(1);
            }}
          >
            Deletion requests
          </Button>
        </div>

        {list.isLoading ? (
          <Spinner label="Loading…" />
        ) : Array.isArray(list.data?.items) && list.data.items.length > 0 ? (
          <div className="overflow-hidden rounded-lg border border-line bg-surface shadow-card">
            <table className="table-base">
              <thead>
                <tr>
                  <th className="w-8">
                    <input
                      type="checkbox"
                      className="cursor-pointer"
                      checked={pageIds.length > 0 && pageIds.every((id) => selected.has(id))}
                      onChange={() => setSelected(toggleAll(selected, pageIds))}
                      aria-label="Select all"
                    />
                  </th>
                  <th>User</th>
                  <th className="w-44">Joined</th>
                  <th className="w-32">Role</th>
                  <th className="w-32 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {list.data.items.map((u) => {
                  const isMe = me?.id === u.id;
                  return (
                    <tr key={u.id} className={`cursor-pointer hover:bg-surface-muted/60 ${selected.has(u.id) ? 'bg-brand/5' : ''}`} onClick={() => navigate(`/users/${u.id}`)}>
                      <td onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          className="cursor-pointer"
                          checked={selected.has(u.id)}
                          onChange={() => setSelected(toggleId(selected, u.id))}
                          aria-label={`Select ${u.email}`}
                        />
                      </td>
                      <td>
                        <div className="flex items-center gap-3">
                          {u.avatarUrl ? (
                            <img
                              src={u.avatarUrl}
                              alt=""
                              className="h-8 w-8 rounded-full ring-1 ring-line"
                              referrerPolicy="no-referrer"
                            />
                          ) : (
                            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-surface-muted text-xs font-medium uppercase text-ink-muted">
                              {u.email.slice(0, 2)}
                            </div>
                          )}
                          <div>
                            <p className="font-medium text-ink">{u.name ?? '—'}</p>
                            <p className="text-xs text-ink-subtle">{u.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="text-sm text-ink-muted">{fmtRelative(u.createdAt)}</td>
                      <td>
                        <Badge
                          variant={u.role === 'admin' ? 'brand' : 'neutral'}
                          className="uppercase"
                        >
                          <ShieldCheck className="h-3 w-3" /> {u.role}
                        </Badge>
                        {u.deletionRequested ? (
                          <Badge variant="outline" className="ml-1.5 border-danger/40 text-danger">
                            Deletion requested
                          </Badge>
                        ) : null}
                      </td>
                      <td className="text-right" onClick={(e) => e.stopPropagation()}>
                        <Button
                          variant={u.role === 'admin' ? 'outline' : 'primary'}
                          size="sm"
                          disabled={
                            setRoleMut.isPending ||
                            (isMe && u.role === 'admin') /* server refuses self-demote */
                          }
                          onClick={async () => {
                            const next: Role = u.role === 'admin' ? 'user' : 'admin';
                            if (
                              !(await confirm({
                                title: `${next === 'admin' ? 'Promote' : 'Demote'} ${u.email} to "${next}"?`,
                                description:
                                  next === 'admin'
                                    ? 'Admins have full access to every part of the panel.'
                                    : 'Removes this account’s admin access.',
                                confirmLabel: next === 'admin' ? 'Promote' : 'Demote',
                                variant: 'primary',
                              }))
                            )
                              return;
                            try {
                              await setRoleMut.mutateAsync({ id: u.id, role: next });
                              showToast({ title: `Set to ${next}`, variant: 'success' });
                            } catch (err) {
                              showToast({
                                title: 'Failed',
                                description: (err as Error).message,
                                variant: 'danger',
                              });
                            }
                          }}
                        >
                          {u.role === 'admin' ? 'Demote to user' : 'Make admin'}
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div className="flex items-center justify-between border-t border-line px-4 py-3 text-sm text-ink-muted">
              <span>
                Page {page} of {totalPages}
              </span>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => p - 1)}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <Empty title="No users" description="No users match your filters." />
        )}
      </div>
      <BulkBar
        selected={selected}
        onClear={() => setSelected(new Set())}
        entityLabel="user"
        deleting={bulkDelete.isPending}
        onDelete={async () => {
          if (
            !(await confirm({
              title: `Permanently delete ${selected.size} user(s)?`,
              description: 'This cannot be undone.',
              confirmLabel: 'Delete',
            }))
          )
            return;
          try {
            const result = await bulkDelete.mutateAsync([...selected]);
            showToast({ title: `${result.deleted} user(s) deleted`, variant: 'success' });
            setSelected(new Set());
          } catch (err) {
            showToast({ title: 'Delete failed', description: (err as Error).message, variant: 'danger' });
          }
        }}
      />
      {confirmDialog}
    </>
  );
}
